import { useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

export const inspectorWidth = 340;

// SVG presentation attributes take plain color strings, so the canvas palette is resolved from the theme once
// and handed to the drawing code instead of sx blocks.
export const useEntityGraphColors = () => {
    const theme = useTheme();
    return {
        nodeFill: theme.palette.common.white,
        nodeStroke: theme.palette.grey[400],
        nodeText: theme.palette.text.primary,
        nodeTypeText: theme.palette.grey[500],
        rootStroke: theme.palette.primary.main,
        focusTagFill: theme.palette.primary.main,
        edge: theme.palette.grey[400],
        edgeHover: theme.palette.primary.main,
        upEdge: theme.palette.error.light,
        chipFill: theme.palette.grey[100],
        chipStroke: theme.palette.grey[300],
        chipText: theme.palette.grey[600],
        badgeText: theme.palette.common.white,
    };
};

// MUI DialogContent's default padding — DotLegalDialog has no prop to remove it, so the fullscreen shell
// bleeds over it with negative margins to run edge-to-edge like DocumentPreview.
const dialogContentPaddingY = 20;
const dialogContentPaddingX = 24;

export const useEntityGraphStyles = () => {
    const theme = useTheme();
    return createSxStyles({
        dialogShell: {
            display: "flex",
            flexDirection: "column",
            height: `calc(100% + ${dialogContentPaddingY * 2}px)`,
            margin: `-${dialogContentPaddingY}px -${dialogContentPaddingX}px`,
        },
        container: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
        },
        headerBar: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(2),
            minHeight: 52,
            padding: theme.spacing(1, 2),
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0,
        },
        headerTitle: {
            fontSize: 13,
            fontWeight: theme.typography.fontWeightBold,
            whiteSpace: "nowrap",
        },
        headerControls: {
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.spacing(2),
            flexWrap: "wrap",
        },
        headerClose: {
            color: theme.palette.primary.main,
        },
        toolbarGroup: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
        },
        body: {
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
        },
        canvasWrap: {
            flex: 1,
            minWidth: 0,
            position: "relative",
            overflow: "hidden",
            cursor: "grab",
            backgroundColor: theme.palette.grey[50],
        },
        canvasWrapPanning: {
            cursor: "grabbing",
        },
        // The graph's counterpart to the governance overview's filter paper: a DotLegalPaper floating on the
        // grey canvas so the nested switcher papers get the paper-on-grey contrast they were designed for.
        canvasFilterBar: {
            position: "absolute",
            top: theme.spacing(2),
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            maxWidth: `calc(100% - ${theme.spacing(4)})`,
        },
        canvasFilterBarContent: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(3),
            flexWrap: "wrap",
            rowGap: theme.spacing(1),
        },
        svg: {
            display: "block",
            width: "100%",
            height: "100%",
            touchAction: "none",
            "& .entity-graph-node": {
                cursor: "pointer",
            },
            "& .entity-graph-node:hover rect:first-of-type": {
                stroke: theme.palette.primary.main,
            },
            "& .entity-graph-node:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: "3px",
            },
            "& .entity-graph-edge:hover": {
                opacity: 1,
            },
            "& .entity-graph-edge:hover .entity-graph-edge-line": {
                stroke: theme.palette.primary.main,
                strokeWidth: 2.5,
                opacity: 1,
                markerEnd: "url(#entity-graph-arrow-hover)",
            },
            // System associations are undirected, so they keep no arrowhead even on hover.
            "& .entity-graph-edge-undirected:hover .entity-graph-edge-line": {
                markerEnd: "none",
            },
        },
        zoomControls: {
            position: "absolute",
            right: theme.spacing(2),
            bottom: theme.spacing(2),
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.75),
            alignItems: "center",
        },
        zoomButton: {
            width: 34,
            height: 34,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[400]}`,
            borderRadius: theme.spacing(1),
            "&:hover": {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.common.white,
            },
        },
        zoomLabel: {
            fontSize: "0.65rem",
            color: theme.palette.grey[500],
            fontVariantNumeric: "tabular-nums",
            userSelect: "none",
        },
        emptyState: {
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        inspector: {
            width: inspectorWidth,
            flexShrink: 0,
            borderLeft: `1px solid ${theme.palette.grey[200]}`,
            padding: theme.spacing(2.5),
            overflowY: "auto",
            backgroundColor: theme.palette.common.white,
        },
        inspectorHeading: {
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: theme.palette.grey[500],
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing(1.5),
        },
        inspectorName: {
            fontSize: "1.2rem",
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing(0.5),
            overflowWrap: "anywhere",
        },
        inspectorTypeChip: {
            display: "inline-block",
            fontSize: "0.65rem",
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            color: theme.palette.grey[600],
            backgroundColor: theme.palette.grey[100],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: "5px",
            padding: `2px ${theme.spacing(1)}`,
            marginBottom: theme.spacing(2),
        },
        metricRow: {
            display: "flex",
            gap: theme.spacing(1.25),
            marginBottom: theme.spacing(2),
        },
        metric: {
            flex: 1,
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1.25),
        },
        metricLabel: {
            display: "block",
            fontSize: "0.6rem",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            color: theme.palette.grey[500],
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing(0.5),
        },
        metricValue: {
            fontSize: "1.25rem",
            fontWeight: theme.typography.fontWeightBold,
            fontVariantNumeric: "tabular-nums",
        },
        rowBlock: {
            marginBottom: theme.spacing(2),
        },
        rowBlockLabel: {
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: theme.palette.grey[500],
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing(1),
        },
        depList: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(0.5),
        },
        depItem: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            fontSize: "0.8rem",
            padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: "7px",
            cursor: "pointer",
            textAlign: "left",
            width: "100%",
            "&:hover": {
                borderColor: theme.palette.primary.main,
            },
        },
        depItemLink: {
            textDecoration: "none",
            color: theme.palette.primary.main,
            "&:hover": {
                borderColor: theme.palette.primary.main,
                textDecoration: "underline",
            },
        },
        depItemSwatch: {
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
        },
        depItemName: {
            flex: 1,
            overflowWrap: "anywhere",
        },
        depItemType: {
            fontSize: "0.6rem",
            letterSpacing: "0.08em",
            color: theme.palette.grey[500],
            textTransform: "uppercase",
        },
        depItemHop: {
            fontSize: "0.6rem",
            color: theme.palette.grey[500],
            whiteSpace: "nowrap",
        },
        depEmpty: {
            fontSize: "0.8rem",
            color: theme.palette.grey[500],
            fontStyle: "italic",
        },
    });
};
